import React, { useState } from 'react';
import './ApiQuickstart.css';

const ApiQuickstart = () => {
    const [activeTab, setActiveTab] = useState('python');

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const pythonExample = `import requests

# Configuration
API_KEY = "your_api_key_here"
BASE_URL = "https://api.bookwitch.io/api/v1"

headers = {
    "Content-Type": "application/json",
    "X-API-Key": API_KEY
}

# Book configuration
book_data = {
    "description": "A comprehensive guide to meditation for mental health",
    "chapters": 5,
    "sub_chapters": 3,
    "language": "English",
    "type": "guide",
    "author_name": "Dr. Jane Smith"
}

# Create the book generation task
response = requests.post(f"{BASE_URL}/generate", headers=headers, json=book_data)

if response.status_code == 202:
    result = response.json()
    task_guid = result["guid"]
    print(f"✅ Book generation started! Task ID: {task_guid}")
else:
    print(f"❌ Error: {response.status_code} - {response.text}")`;

    const curlExample = `curl -X POST "https://api.bookwitch.io/api/v1/generate" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: your_api_key_here" \\
  -d '{
    "description": "A comprehensive guide to meditation for mental health",
    "chapters": 5,
    "sub_chapters": 3,
    "language": "English",
    "type": "guide",
    "author_name": "Dr. Jane Smith"
  }'`;

    const statusCheckPython = `import time

def check_status(task_guid):
    while True:
        response = requests.get(f"{BASE_URL}/tasks/{task_guid}", headers=headers)
        
        if response.status_code == 200:
            task = response.json()
            status = task["status"]
            
            if status == "DONE":
                print(f"✅ Book completed: {task['title']}")
                return task
            elif status == "ERROR":
                print("❌ Generation failed")
                return None
            else:
                print(f"⏳ Status: {status}")
                time.sleep(10)  # Check again in 10 seconds
        else:
            print(f"❌ Error checking status: {response.status_code}")
            return None

# Check the status
task = check_status(task_guid)`;

    const downloadPython = `def download_book(task_guid, format="pdf"):
    response = requests.get(
        f"{BASE_URL}/tasks/{task_guid}/export?format={format}",
        headers=headers,
        stream=True
    )
    
    if response.status_code == 200:
        filename = f"my_book.{format}"
        with open(filename, 'wb') as f:
            for chunk in response.iter_content(chunk_size=8192):
                f.write(chunk)
        print(f"✅ Book downloaded: {filename}")
        return True
    else:
        print(f"❌ Download failed: {response.status_code}")
        return False

# Download the book
download_book(task_guid, "pdf")`;

    const completeExample = `import requests
import time
import sys

class BookWitchClient:
    def __init__(self, api_key):
        self.api_key = api_key
        self.base_url = "https://api.bookwitch.io/api/v1"
        self.headers = {
            "Content-Type": "application/json",
            "X-API-Key": api_key
        }
    
    def generate_book(self, **kwargs):
        """Generate a new book"""
        response = requests.post(f"{self.base_url}/generate", 
                               headers=self.headers, json=kwargs)
        
        if response.status_code == 202:
            return response.json()["guid"]
        else:
            raise Exception(f"Generation failed: {response.status_code} - {response.text}")
    
    def wait_for_completion(self, task_guid, check_interval=10):
        """Wait for book generation to complete"""
        print(f"⏳ Waiting for book generation to complete...")
        
        while True:
            response = requests.get(f"{self.base_url}/tasks/{task_guid}", 
                                  headers=self.headers)
            
            if response.status_code != 200:
                raise Exception(f"Status check failed: {response.status_code}")
            
            task = response.json()
            status = task["status"]
            
            if status == "DONE":
                print(f"✅ Book completed: {task['title']}")
                return task
            elif status == "ERROR":
                raise Exception("Book generation failed")
            
            print(f"📚 Status: {status}")
            time.sleep(check_interval)
    
    def download_book(self, task_guid, format="pdf", filename=None):
        """Download the generated book"""
        response = requests.get(
            f"{self.base_url}/tasks/{task_guid}/export?format={format}",
            headers=self.headers,
            stream=True
        )
        
        if response.status_code != 200:
            raise Exception(f"Download failed: {response.status_code}")
        
        if not filename:
            filename = f"book_{task_guid}.{format}"
        
        with open(filename, 'wb') as f:
            for chunk in response.iter_content(chunk_size=8192):
                f.write(chunk)
        
        print(f"✅ Book downloaded: {filename}")
        return filename

# Usage example
if __name__ == "__main__":
    # Initialize client
    client = BookWitchClient("your_api_key_here")
    
    # Generate a book
    task_guid = client.generate_book(
        description="A beginner's guide to sustainable living and eco-friendly practices",
        chapters=6,
        sub_chapters=4,
        language="English",
        type="guide",
        author_name="Green Living Expert"
    )
    
    print(f"📖 Book generation started. Task ID: {task_guid}")
    
    # Wait for completion
    task = client.wait_for_completion(task_guid)
    
    # Download in multiple formats
    client.download_book(task_guid, "pdf")
    client.download_book(task_guid, "docx")
    
    print("🎉 All done!")`;

    const CodeBlock = ({ code, language = 'python' }) => (
        <div className="code-block">
            <div className="code-header">
                <span className="language-label">{language}</span>
                <button 
                    className="copy-button"
                    onClick={() => copyToClipboard(code)}
                    title="Copy to clipboard"
                >
                    📋 Copy
                </button>
            </div>
            <pre><code>{code}</code></pre>
        </div>
    );

    const JsonBlock = ({ json }) => (
        <div className="code-block">
            <div className="code-header">
                <span className="language-label">json</span>
            </div>
            <pre><code>{JSON.stringify(json, null, 2)}</code></pre>
        </div>
    );

    return (
        <div className="api-quickstart-container">
            <div className="api-quickstart-content">
                <div className="hero-section">
                    <h1>📚 BookWitch API - Quick Start Guide</h1>
                    <p className="hero-description">
                        Get up and running with the BookWitch API in minutes. This guide will walk you through 
                        generating your first book with practical examples.
                    </p>
                </div>

                <div className="info-grid">
                    <div className="info-card">
                        <div className="info-icon">🔑</div>
                        <h3>Prerequisites</h3>
                        <ul>
                            <li>An API key (contact us to get one)</li>
                            <li>Basic understanding of HTTP requests</li>
                        </ul>
                    </div>
                    <div className="info-card">
                        <div className="info-icon">🌐</div>
                        <h3>Base URL</h3>
                        <code className="inline-code">https://api.bookwitch.io/api/v1</code>
                    </div>
                    <div className="info-card">
                        <div className="info-icon">🔐</div>
                        <h3>Authentication</h3>
                        <p>Include your API key in the <code className="inline-code">X-API-Key</code> header</p>
                    </div>
                </div>

                <div className="section">
                    <h2>🚀 Step 1: Generate a Book</h2>
                    <p>Create a new book generation task by sending a POST request to <code className="inline-code">/generate</code>.</p>
                    
                    <div className="tab-container">
                        <div className="tab-buttons">
                            <button 
                                className={`tab-button ${activeTab === 'python' ? 'active' : ''}`}
                                onClick={() => setActiveTab('python')}
                            >
                                🐍 Python
                            </button>
                            <button 
                                className={`tab-button ${activeTab === 'curl' ? 'active' : ''}`}
                                onClick={() => setActiveTab('curl')}
                            >
                                💻 cURL
                            </button>
                        </div>
                        
                        <div className="tab-content">
                            {activeTab === 'python' && <CodeBlock code={pythonExample} language="python" />}
                            {activeTab === 'curl' && <CodeBlock code={curlExample} language="bash" />}
                        </div>
                    </div>

                    <div className="response-section">
                        <h4>📤 Response</h4>
                        <JsonBlock json={{
                            "guid": "abc123def456789",
                            "status": "pending",
                            "status_url": "/api/v1/tasks/abc123def456789"
                        }} />
                    </div>
                </div>

                <div className="section">
                    <h2>🔍 Step 2: Check Generation Status</h2>
                    <p>Monitor your book generation progress by checking the task status.</p>
                    
                    <CodeBlock code={statusCheckPython} language="python" />
                    
                    <div className="response-examples">
                        <div className="response-example">
                            <h4>⏳ In Progress Response</h4>
                            <JsonBlock json={{
                                "guid": "abc123def456789",
                                "status": "IN_PROGRESS",
                                "title": "Untitled",
                                "created_date": 1703123456.789,
                                "description": "A comprehensive guide to meditation for mental health",
                                "language": "English",
                                "export_formats": []
                            }} />
                        </div>
                        
                        <div className="response-example">
                            <h4>✅ Completed Response</h4>
                            <JsonBlock json={{
                                "guid": "abc123def456789",
                                "status": "DONE",
                                "title": "The Complete Guide to Meditation for Mental Wellness",
                                "created_date": 1703123456.789,
                                "description": "A comprehensive guide to meditation for mental health",
                                "language": "English",
                                "export_formats": ["pdf", "txt", "docx"]
                            }} />
                        </div>
                    </div>
                </div>

                <div className="section">
                    <h2>📥 Step 3: Download Your Book</h2>
                    <p>Once generation is complete, download your book in your preferred format.</p>
                    
                    <CodeBlock code={downloadPython} language="python" />
                    
                    <div className="download-formats">
                        <h4>Available Formats</h4>
                        <div className="format-grid">
                            <div className="format-card">
                                <div className="format-icon">📄</div>
                                <strong>PDF</strong>
                                <small>Perfect for reading and printing</small>
                            </div>
                            <div className="format-card">
                                <div className="format-icon">📝</div>
                                <strong>DOCX</strong>
                                <small>Editable Word document</small>
                            </div>
                            <div className="format-card">
                                <div className="format-icon">📃</div>
                                <strong>TXT</strong>
                                <small>Plain text format</small>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="section">
                    <h2>🔧 Complete Python Client Example</h2>
                    <p>Here's a complete script that generates a book and downloads it:</p>
                    
                    <CodeBlock code={completeExample} language="python" />
                </div>

                <div className="section">
                    <h2>🔔 Using Webhooks for Real-time Updates</h2>
                    <p>Instead of polling for status updates, you can receive real-time notifications via webhooks:</p>
                    
                    <div className="webhook-info">
                        <div className="webhook-card">
                            <h4>📍 IN_PROGRESS</h4>
                            <p>When generation starts</p>
                        </div>
                        <div className="webhook-card">
                            <h4>✅ DONE</h4>
                            <p>When generation completes (with book title)</p>
                        </div>
                        <div className="webhook-card">
                            <h4>❌ ERROR</h4>
                            <p>If generation fails (with error details)</p>
                        </div>
                    </div>
                    
                    <CodeBlock code={`# Add callback_url to your generation request
book_data = {
    "description": "A comprehensive guide to meditation for mental health",
    "chapters": 5,
    "sub_chapters": 3,
    "language": "English",
    "type": "guide",
    "author_name": "Dr. Jane Smith",
    "callback_url": "https://your-domain.com/webhooks/bookwitch"
}`} language="python" />
                    
                    <p>For webhook implementation details, see our <a href="/api/webhooks" className="link">Webhooks Guide</a>.</p>
                </div>

                <div className="section">
                    <h2>📖 Book Types</h2>
                    <p>Choose the appropriate book type for your content:</p>
                    
                    <div className="book-types-grid">
                        <div className="book-type-card">
                            <div className="book-type-icon">📚</div>
                            <h4>guide</h4>
                            <p>How-to guides, tutorials, instruction manuals</p>
                        </div>
                        <div className="book-type-card">
                            <div className="book-type-icon">📖</div>
                            <h4>fiction</h4>
                            <p>Novels, short stories, creative writing</p>
                        </div>
                        <div className="book-type-card">
                            <div className="book-type-icon">📝</div>
                            <h4>essay</h4>
                            <p>Academic papers, opinion pieces, analytical writing</p>
                        </div>
                        <div className="book-type-card">
                            <div className="book-type-icon">📊</div>
                            <h4>informative</h4>
                            <p>Educational content, reference materials, factual content</p>
                        </div>
                    </div>
                </div>

                <div className="section">
                    <h2>⚠️ Error Handling</h2>
                    <p>Common HTTP status codes you might encounter:</p>
                    
                    <div className="error-codes">
                        <div className="error-code success">
                            <strong>202</strong> Generation request accepted
                        </div>
                        <div className="error-code client-error">
                            <strong>400</strong> Bad request (invalid parameters)
                        </div>
                        <div className="error-code client-error">
                            <strong>401</strong> Unauthorized (invalid API key)
                        </div>
                        <div className="error-code client-error">
                            <strong>403</strong> Forbidden (insufficient permissions)
                        </div>
                        <div className="error-code client-error">
                            <strong>404</strong> Task not found
                        </div>
                        <div className="error-code server-error">
                            <strong>500</strong> Internal server error
                        </div>
                    </div>
                </div>

                <div className="section">
                    <h2>🚦 Rate Limits</h2>
                    <div className="rate-limits">
                        <div className="rate-limit-item">
                            <strong>Generation requests:</strong> 10 per minute
                        </div>
                        <div className="rate-limit-item">
                            <strong>Status checks:</strong> 60 per minute
                        </div>
                        <div className="rate-limit-item">
                            <strong>Downloads:</strong> 30 per minute
                        </div>
                    </div>
                </div>

                <div className="help-section">
                    <h2>🆘 Need Help?</h2>
                    <div className="help-links">
                        <a href="https://api.bookwitch.io/apidocs/" className="help-link" target="_blank" rel="noopener noreferrer">
                            📚 Full API Documentation
                        </a>
                        <a href="/api/webhooks" className="help-link">
                            🔗 Webhooks Guide
                        </a>
                        <a href="/contact" className="help-link">
                            💌 Contact Support
                        </a>
                    </div>
                </div>

                <div className="cta-section">
                    <h2>🎉 Ready to Start?</h2>
                    <p>Copy one of the examples above and start creating your first book!</p>
                    <div className="cta-buttons">
                        <a href="/signup" className="cta-button primary">
                            Get API Key
                        </a>
                        <a href="https://api.bookwitch.io/apidocs/" className="cta-button secondary" target="_blank" rel="noopener noreferrer">
                            View Full Docs
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApiQuickstart; 